import {
  BATTLE_MODES,
  BUILDING_KEYS,
  ENEMY_RECRUITMENT_UNIT_LEAD_LIMIT,
  TURN_SIDES
} from "../core/constants.js";
import { getBuildingIncomeForSide } from "../core/economy.js";
import { randomInt } from "../core/random.js";
import { getCommanderById } from "../content/commanders.js";
import { appendLog } from "./battleLog.js";
import { applyBuildingSupply } from "./battleServicing.js";
import { findUnitById } from "./battleUnits.js";
import { captureBuildingForUnit } from "./captureRules.js";
import {
  applyMissionTurnEnd,
  canUnitSabotageDefendTarget,
  performSabotageDefendTarget,
  updateMissionVictory
} from "./missionRules.js";
import {
  canSlipstreamAfterAttack,
  expireCurrentTurnStatuses,
  getIncomeBonus,
  getMovementModifier,
  tickSideStatuses
} from "./commanderEffects.js";
import { getAttackRangeCap, removeDeadUnits } from "./combatResolver.js";
import { applyRunCardTurnStartEffects, isUnitZombified } from "./runCardEffects.js";
import {
  getBestCapturePlan,
  getBestMoveAttackOption,
  getBestRepairPlan,
  getBestRunnerTransportPlan,
  getBestSupportPlan,
  getEnemyRecruitmentLimit,
  getEnemyRecruitmentMapCap,
  hasEnemyAttackOpportunity,
  isUnitPinnedByThreat,
  planEnemyTurn,
  pickBestFavorableAttack,
  pickBestAvailableAttack,
  pickEnemyRecruitmentCandidate,
  pickEnemySlipstreamTile,
  pickFallbackMovementTile
} from "./enemyAi.js";
import {
  canUnitOccupyTile,
  canUnitAttackTarget,
  getAttackProfileForTarget,
  getLivingUnits,
  getMovementPathCost,
  getMovementPath,
  getReachableTiles,
  getUnitAt
} from "./selectors.js";
import { createUnitFromType } from "./unitFactory.js";
import {
  deployPreparedReinforcement,
  prepareEnemyTurnEndReinforcements,
  resolveReinforcementTriggers
} from "./reinforcementRules.js";

const PRODUCTION_BUILDING_TYPES = ["barracks", "motor-pool", "airfield"];

function snapshotUnitResources(state) {
  return new Map(
    [...state.player.units, ...state.enemy.units].map((unit) => [
      unit.id,
      {
        hp: unit.current.hp,
        ammo: unit.current.ammo,
        stamina: unit.current.stamina,
        hadBurn: (unit.statuses ?? []).some((status) => status.type === "burn")
      }
    ])
  );
}

function recordStatusAndPassiveChanges(system, before, side) {
  for (const unit of [...system.state.player.units, ...system.state.enemy.units]) {
    const previous = before.get(unit.id);
    if (!previous) {
      continue;
    }

    if (unit.current.hp < previous.hp && previous.hadBurn) {
      system.recordPresentationEvent("status", {
        action: "tick",
        targetId: unit.id,
        targetUnitTypeId: unit.unitTypeId,
        owner: unit.owner,
        statusType: "burn",
        damage: previous.hp - unit.current.hp,
        x: unit.x,
        y: unit.y
      });
    }

    if (unit.current.hp > previous.hp) {
      system.recordPresentationEvent("service", {
        actorId: null,
        targetId: unit.id,
        targetUnitTypeId: unit.unitTypeId,
        owner: side,
        sourceKind: "commander-passive",
        sourceId: "atlas-field-repairs",
        buildingType: null,
        hpRecovered: unit.current.hp - previous.hp,
        ammoRecovered: Math.max(0, unit.current.ammo - previous.ammo),
        staminaRecovered: Math.max(0, unit.current.stamina - previous.stamina),
        x: unit.x,
        y: unit.y
      });
    }
  }
}

function recordRunCardResourceChanges(system, before, side) {
  for (const unit of system.state[side].units) {
    const previous = before.get(unit.id);
    if (!previous) {
      continue;
    }

    const damage = Math.max(0, previous.hp - unit.current.hp);
    if (damage > 0) {
      system.recordPresentationEvent("status", {
        action: "effect-damage",
        actorId: null,
        targetId: unit.id,
        targetUnitTypeId: unit.unitTypeId,
        owner: side,
        sourceKind: "run-card",
        sourceId: "glass-fuel-lines",
        statusType: null,
        damage,
        killed: unit.current.hp <= 0,
        x: unit.x,
        y: unit.y
      });
    }

    const hpRecovered = Math.max(0, unit.current.hp - previous.hp);
    const ammoRecovered = Math.max(0, unit.current.ammo - previous.ammo);
    const staminaRecovered = Math.max(0, unit.current.stamina - previous.stamina);
    if (hpRecovered + ammoRecovered + staminaRecovered === 0) {
      continue;
    }

    system.recordPresentationEvent("service", {
      actorId: null,
      targetId: unit.id,
      targetUnitTypeId: unit.unitTypeId,
      owner: side,
      sourceKind: "run-card",
      sourceId: "turn-start-run-card",
      buildingType: null,
      hpRecovered,
      ammoRecovered,
      staminaRecovered,
      x: unit.x,
      y: unit.y
    });
  }
}

function getActionableUnitsForSide(state, side) {
  return state[side].units.filter((unit) => unit.current.hp > 0);
}

function getZombifiedTargetsForUnit(state, unit) {
  const units = [...state.player.units, ...state.enemy.units];

  return units.filter((target) => {
    if (
      !target ||
      target.id === unit.id ||
      target.current.hp <= 0 ||
      target.transport?.carriedByUnitId ||
      isUnitZombified(target)
    ) {
      return false;
    }

    const attackProfile = getAttackProfileForTarget(unit, target);

    if (!attackProfile || !canUnitAttackTarget(unit, target)) {
      return false;
    }

    const distance = Math.abs(unit.x - target.x) + Math.abs(unit.y - target.y);
    const rangeCap = getAttackRangeCap(state, unit, attackProfile);
    return distance >= attackProfile.minRange && distance <= rangeCap;
  });
}

function pickZombifiedTarget(state, unit) {
  const targets = getZombifiedTargetsForUnit(state, unit);

  if (targets.length === 0) {
    return null;
  }

  const roll = randomInt(state.seed, 0, targets.length - 1);
  state.seed = roll.seed;
  return targets[roll.value];
}

export function tickUnitDurations(system, side) {
  for (const unit of getLivingUnits(system.state, side)) {
    for (const [key, turns] of Object.entries(unit.cooldowns ?? {})) {
      if (turns > 0) {
        unit.cooldowns[key] = turns - 1;
      }
    }
    if (unit.unitTypeId === "runner" && unit.transport) {
      unit.transport.canUnloadAfterMove = false;
      unit.transport.hasLockedUnload = false;
    }
  }
}

export function endTurn(system) {
  if (system.state.victory) {
    return false;
  }

  if (system.state.pendingAction) {
    appendLog(system.state, "Resolve or cancel the current action before ending the turn.");
    return false;
  }

  if (system.state.turn.activeSide === TURN_SIDES.PLAYER) {
    expireCurrentTurnStatuses(system.state, TURN_SIDES.PLAYER);
    system.state.turn.activeSide = TURN_SIDES.ENEMY;
    system.state.turn.number += 1;
    system.clearSelection();
    system.state.enemyTurn = {
      started: false,
      pendingAttack: null,
      pendingSlipstream: null,
      pendingUnitIds: [],
      pendingReinforcementDeployments: [],
      plannedActions: [],
      plannedPendingUnitIdsKey: "",
      forcePassed: false
    };
    applyMissionTurnEnd(system.state, TURN_SIDES.PLAYER);
    system.updateVictoryState();
    return true;
  }

  return false;
}

export function isEnemyTurnActive(system) {
  return system.state.turn.activeSide === TURN_SIDES.ENEMY && !system.state.victory;
}

export function startEnemyTurnActions(system) {
  if (!system.state.enemyTurn || !isEnemyTurnActive(system) || system.state.enemyTurn.started) {
    return false;
  }

  system.state.enemyTurn.started = true;
  const beforeStatusTick = snapshotUnitResources(system.state);
  let beforeEnemyPassive = beforeStatusTick;
  tickSideStatuses(system.state, TURN_SIDES.ENEMY, {
    onAfterStatuses: () => {
      recordStatusAndPassiveChanges(system, beforeStatusTick, TURN_SIDES.ENEMY);
      beforeEnemyPassive = snapshotUnitResources(system.state);
    }
  });
  recordStatusAndPassiveChanges(system, beforeEnemyPassive, TURN_SIDES.ENEMY);
  tickUnitDurations(system, TURN_SIDES.ENEMY);
  const incomeGain = collectIncome(system, TURN_SIDES.ENEMY);
  resetActions(system, TURN_SIDES.ENEMY);
  system.state.enemyTurn.pendingUnitIds = getActionableUnitsForSide(system.state, TURN_SIDES.ENEMY)
    .filter((unit) => !unit.hasMoved && !unit.hasAttacked && !unit.transport?.carriedByUnitId)
    .map((unit) => unit.id);
  system.updateVictoryState();
  return {
    changed: true,
    incomeGain,
    reinforcementActivations: []
  };
}

export function hasPendingEnemyTurn(system) {
  return Boolean(
    system.state.enemyTurn?.pendingAttack ||
      system.state.enemyTurn?.pendingSlipstream ||
      system.state.enemyTurn?.pendingUnitIds?.length
  );
}

export function forcePassEnemyTurn(system, reason = "safety") {
  if (!system.state.enemyTurn || system.state.turn.activeSide !== TURN_SIDES.ENEMY || system.state.victory) {
    return {
      changed: false,
      reason
    };
  }

  system.state.enemyTurn.pendingAttack = null;
  system.state.enemyTurn.pendingSlipstream = null;
  system.state.enemyTurn.pendingUnitIds = [];
  system.state.enemyTurn.pendingReinforcementDeployments = [];
  system.state.enemyTurn.plannedActions = [];
  system.state.enemyTurn.plannedPendingUnitIdsKey = "";
  system.state.enemyTurn.forcePassed = true;
  system.clearSelection();
  appendLog(system.state, "Enemy command stalled. Enemy passed the turn.");

  return {
    changed: true,
    reason
  };
}

function moveEnemyUnit(system, unit, tile, movementBudget) {
  const moved = tile && (tile.x !== unit.x || tile.y !== unit.y);

  if (!moved) {
    return {
      moved: false,
      moveSegments: 0
    };
  }

  if (!canUnitOccupyTile(system.state, unit, tile.x, tile.y)) {
    return {
      moved: false,
      moveSegments: 0
    };
  }

  const movePath = moved
    ? getMovementPath(system.state, unit, movementBudget, tile.x, tile.y)
    : [];
  const moveCost = getMovementPathCost(
    system.state,
    unit,
    movementBudget,
    tile.x,
    tile.y
  );

  if (moveCost === null || movePath.length < 2) {
    return {
      moved: false,
      moveSegments: 0
    };
  }

  const moveSegments = Math.max(0, movePath.length - 1);

  unit.x = tile.x;
  unit.y = tile.y;
  unit.movedThisTurn = true;
  unit.current.stamina = Math.max(0, unit.current.stamina - moveCost);
  if (unit.unitTypeId === "runner" && unit.transport?.carryingUnitId) {
    unit.transport.canUnloadAfterMove = true;
  }
  system.syncTransportCargoPosition(unit);
  unit.hasMoved = true;
  system.state.selection = { type: "unit", id: unit.id, x: unit.x, y: unit.y };

  return {
    moved: true,
    moveSegments
  };
}

function areTilesEqual(left, right) {
  return Boolean(left && right && left.x === right.x && left.y === right.y);
}

function queueEnemySlipstreamMove(system, unitId, projectedPostAttackTile = undefined) {
  const unit = findUnitById(system.state, unitId);

  if (!unit || !canSlipstreamAfterAttack(system.state, unit) || unit.transport?.carriedByUnitId) {
    if (projectedPostAttackTile !== undefined && projectedPostAttackTile !== null) {
      clearCachedEnemyTurnPlan(system.state.enemyTurn);
    }
    return null;
  }

  const reachableTiles = getReachableTiles(system.state, unit, 1);

  if (reachableTiles.length === 0) {
    if (projectedPostAttackTile !== undefined) {
      clearCachedEnemyTurnPlan(system.state.enemyTurn);
    }
    return null;
  }

  const slipstreamTile = pickEnemySlipstreamTile(system.state, unit, reachableTiles);
  const livePostAttackTile = slipstreamTile ?? { x: unit.x, y: unit.y };

  if (
    projectedPostAttackTile !== undefined &&
    !areTilesEqual(projectedPostAttackTile, livePostAttackTile)
  ) {
    clearCachedEnemyTurnPlan(system.state.enemyTurn);
  }

  if (!slipstreamTile || (slipstreamTile.x === unit.x && slipstreamTile.y === unit.y)) {
    return null;
  }

  const movePath = getMovementPath(system.state, unit, 1, slipstreamTile.x, slipstreamTile.y);
  const moveSegments = Math.max(0, movePath.length - 1);

  system.state.enemyTurn.pendingSlipstream = {
    unitId,
    x: slipstreamTile.x,
    y: slipstreamTile.y,
    moveSegments
  };

  return system.state.enemyTurn.pendingSlipstream;
}

function performQueuedEnemySlipstreamMove(system) {
  const queuedSlipstream = system.state.enemyTurn?.pendingSlipstream;

  if (!queuedSlipstream) {
    return null;
  }

  system.state.enemyTurn.pendingSlipstream = null;
  const unit = findUnitById(system.state, queuedSlipstream.unitId);

  if (!unit || !canSlipstreamAfterAttack(system.state, unit) || unit.transport?.carriedByUnitId) {
    clearCachedEnemyTurnPlan(system.state.enemyTurn);
    return null;
  }

  if (unit.x === queuedSlipstream.x && unit.y === queuedSlipstream.y) {
    clearCachedEnemyTurnPlan(system.state.enemyTurn);
    return null;
  }

  if (
    !canUnitOccupyTile(system.state, unit, queuedSlipstream.x, queuedSlipstream.y) ||
    getMovementPathCost(
      system.state,
      unit,
      1,
      queuedSlipstream.x,
      queuedSlipstream.y
    ) === null
  ) {
    clearCachedEnemyTurnPlan(system.state.enemyTurn);
    return null;
  }

  unit.x = queuedSlipstream.x;
  unit.y = queuedSlipstream.y;
  unit.movedThisTurn = true;
  unit.hasMoved = true;
  if (unit.unitTypeId === "runner" && unit.transport?.carryingUnitId) {
    system.syncTransportCargoPosition(unit);
  }
  appendLog(system.state, `${unit.name} slipped into a new position after attacking.`);

  return {
    changed: true,
    done: system.state.victory || !hasPendingEnemyTurn(system),
    type: "move",
    unitId: queuedSlipstream.unitId,
    moveSegments: queuedSlipstream.moveSegments
  };
}

function resolveEnemyCapturePlan(system, unit, capturePlan, movementBudget) {
  const movement = moveEnemyUnit(system, unit, capturePlan.tile, movementBudget);

  if (capturePlan.canCaptureAfterMove) {
    captureBuildingForUnit(system.state, unit, capturePlan.building);
    system.recordPresentationEvent("mission", {
      action: "capture",
      actorId: unit.id,
      actorUnitTypeId: unit.unitTypeId,
      owner: unit.owner,
      buildingId: capturePlan.building.id,
      buildingType: capturePlan.building.type,
      x: capturePlan.building.x,
      y: capturePlan.building.y
    });
    system.updateVictoryState();
  } else if (movement.moved) {
    unit.hasAttacked = true;
  }

  if (movement.moved || capturePlan.canCaptureAfterMove) {
    return {
      changed: true,
      done: system.state.victory || system.state.enemyTurn.pendingUnitIds.length === 0,
      type: movement.moved ? "move" : "capture",
      unitId: unit.id,
      moveSegments: movement.moveSegments
    };
  }

  return null;
}

function resolveEnemyRepairPlan(system, unit, repairPlan, movementBudget) {
  const movement = moveEnemyUnit(system, unit, repairPlan.tile, movementBudget);
  const supplyResult = applyBuildingSupply(system.state, unit, repairPlan.building, {
    spendAction: true,
    log: true
  });

  if (!supplyResult.changed) {
    return null;
  }

  system.recordPresentationEvent("service", {
    actorId: unit.id,
    actorUnitTypeId: unit.unitTypeId,
    targetId: unit.id,
    targetUnitTypeId: unit.unitTypeId,
    owner: unit.owner,
    sourceKind: "building",
    sourceId: repairPlan.building.id,
    buildingType: repairPlan.building.type,
    hpRecovered: supplyResult.hpRecovered,
    ammoRecovered: supplyResult.ammoRecovered,
    staminaRecovered: supplyResult.staminaRecovered,
    x: unit.x,
    y: unit.y
  });

  unit.cooldowns.repairMode = Math.max(
    unit.cooldowns.repairMode ?? 0,
    repairPlan.canRepairAfterMove ? (movement.moved ? 2 : 1) : 2
  );

  return {
    changed: true,
    done: system.state.victory || system.state.enemyTurn.pendingUnitIds.length === 0,
    type: movement.moved ? "move" : "repair",
    unitId: unit.id,
    moveSegments: movement.moveSegments
  };
}

function takePendingEnemyUnitId(enemyTurn, preferredUnitId = null) {
  if (preferredUnitId) {
    const preferredIndex = enemyTurn.pendingUnitIds.indexOf(preferredUnitId);

    if (preferredIndex >= 0) {
      enemyTurn.pendingUnitIds.splice(preferredIndex, 1);
      return preferredUnitId;
    }
  }

  return enemyTurn.pendingUnitIds.shift() ?? null;
}

function getPendingUnitIdsKey(pendingUnitIds = []) {
  return pendingUnitIds.join("|");
}

function clearCachedEnemyTurnPlan(enemyTurn) {
  if (!enemyTurn) {
    return;
  }

  enemyTurn.plannedActions = [];
  enemyTurn.plannedPendingUnitIdsKey = "";
}

function clonePlannedAction(action) {
  return action ? structuredClone(action) : null;
}

function isPlannedActionReachable(state, unit, action, movementBudget) {
  const tile = action?.tile;

  if (!tile || !Number.isFinite(tile.x) || !Number.isFinite(tile.y)) {
    return false;
  }

  if (!canUnitOccupyTile(state, unit, tile.x, tile.y)) {
    return false;
  }

  if (unit.x === tile.x && unit.y === tile.y) {
    return true;
  }

  return getMovementPathCost(state, unit, movementBudget, tile.x, tile.y) !== null;
}

function isCachedPlannedActionValid(state, action) {
  if (!action || !state.enemyTurn?.pendingUnitIds?.includes(action.unitId)) {
    return false;
  }

  const unit = findUnitById(state, action.unitId);

  if (
    !unit ||
    unit.current.hp <= 0 ||
    unit.transport?.carriedByUnitId ||
    (unit.hasMoved && unit.hasAttacked)
  ) {
    return false;
  }

  const movementBudget = unit.stats.movement + getMovementModifier(state, unit);

  if (!isPlannedActionReachable(state, unit, action, movementBudget)) {
    return false;
  }

  if (action.type === "attack") {
    const target = findUnitById(state, action.targetId);

    if (!target || target.current.hp <= 0 || target.owner === unit.owner) {
      return false;
    }

    return canUnitAttackTarget(
      {
        ...unit,
        x: action.tile.x,
        y: action.tile.y
      },
      target
    );
  }

  if (action.type === "capture" || action.type === "repair") {
    return Boolean(
      action.buildingId &&
        state.map.buildings.some((building) => building.id === action.buildingId)
    );
  }

  return false;
}

function getCachedPlannedAction(system) {
  const enemyTurn = system.state.enemyTurn;
  const cachedActions = enemyTurn?.plannedActions ?? [];

  if (cachedActions.length === 0) {
    return null;
  }

  if (enemyTurn.plannedPendingUnitIdsKey !== getPendingUnitIdsKey(enemyTurn.pendingUnitIds)) {
    clearCachedEnemyTurnPlan(enemyTurn);
    return null;
  }

  const action = cachedActions[0];

  if (!isCachedPlannedActionValid(system.state, action)) {
    clearCachedEnemyTurnPlan(enemyTurn);
    return null;
  }

  return action;
}

function updateCachedEnemyTurnPlan(enemyTurn, plannedAction, plannedTurn = null) {
  if (!enemyTurn || !plannedAction) {
    clearCachedEnemyTurnPlan(enemyTurn);
    return;
  }

  if (plannedTurn?.sequence?.length) {
    enemyTurn.plannedActions = plannedTurn.sequence.slice(1).map(clonePlannedAction).filter(Boolean);
  } else if (
    enemyTurn.plannedActions?.[0]?.unitId === plannedAction.unitId &&
    enemyTurn.plannedActions?.[0]?.type === plannedAction.type
  ) {
    enemyTurn.plannedActions.shift();
  } else {
    clearCachedEnemyTurnPlan(enemyTurn);
    return;
  }

  enemyTurn.plannedPendingUnitIdsKey = enemyTurn.plannedActions.length
    ? getPendingUnitIdsKey(enemyTurn.pendingUnitIds)
    : "";
}

function resolveEnemyTransportPlan(system, unit, transportPlan, movementBudget) {
  if (!transportPlan) {
    return null;
  }

  if (transportPlan.passengerId && !unit.transport?.carryingUnitId) {
    const passenger = findUnitById(system.state, transportPlan.passengerId);

    if (!passenger || !system.boardUnitIntoRunner(passenger, unit)) {
      return null;
    }
  }

  const movement = moveEnemyUnit(system, unit, transportPlan.moveTile, movementBudget);
  const unloaded = transportPlan.unloadTile
    ? system.unloadTransportForEnemy(unit, transportPlan.unloadTile)
    : false;

  if (!unloaded) {
    unit.hasAttacked = true;
  }

  if (movement.moved || unloaded) {
    return {
      changed: true,
      done: system.state.victory || system.state.enemyTurn.pendingUnitIds.length === 0,
      type: movement.moved ? "move" : "unload",
      unitId: unit.id,
      moveSegments: movement.moveSegments
    };
  }

  return null;
}

export function processEnemyTurnStep(system) {
  if (!system.state.enemyTurn || system.state.turn.activeSide !== TURN_SIDES.ENEMY || system.state.victory) {
    return { changed: false, done: true };
  }

  // Enemy turns are resolved as move and attack phases so the renderer can
  // let movement finish before combat begins.
  if (system.state.enemyTurn.pendingAttack) {
    const queuedAttack = system.state.enemyTurn.pendingAttack;
    system.state.enemyTurn.pendingAttack = null;
    const changed = system.attackTarget(queuedAttack.attackerId, queuedAttack.targetId);

    if (changed) {
      queueEnemySlipstreamMove(
        system,
        queuedAttack.attackerId,
        queuedAttack.postAttackTile
      );
      return {
        changed: true,
        done: system.state.victory || !hasPendingEnemyTurn(system),
        type: "attack",
        unitId: queuedAttack.attackerId
      };
    }

    clearCachedEnemyTurnPlan(system.state.enemyTurn);
  }

  if (system.state.enemyTurn.pendingSlipstream) {
    const slipstreamStep = performQueuedEnemySlipstreamMove(system);

    if (slipstreamStep) {
      return slipstreamStep;
    }
  }

  while (system.state.enemyTurn.pendingUnitIds.length > 0) {
    let plannedTurn = null;
    let plannedAction = getCachedPlannedAction(system);

    if (!plannedAction) {
      plannedTurn = planEnemyTurn(
        system.state,
        system.state.enemyTurn.pendingUnitIds
      );
      plannedAction = plannedTurn?.action ?? null;
    }

    const unitId = takePendingEnemyUnitId(
      system.state.enemyTurn,
      plannedAction?.unitId
    );
    const unit = findUnitById(system.state, unitId);

    if (plannedAction?.unitId === unitId) {
      updateCachedEnemyTurnPlan(system.state.enemyTurn, plannedAction, plannedTurn);
    } else {
      clearCachedEnemyTurnPlan(system.state.enemyTurn);
    }

    if (
      !unit ||
      unit.current.hp <= 0 ||
      unit.transport?.carriedByUnitId ||
      (unit.hasMoved && unit.hasAttacked)
    ) {
      continue;
    }

    system.state.selection = { type: "unit", id: unit.id, x: unit.x, y: unit.y };

    if (isUnitZombified(unit)) {
      const target = pickZombifiedTarget(system.state, unit);

      if (target && system.attackTarget(unit.id, target.id)) {
        appendLog(system.state, `${unit.name} lashed out while zombified.`);
        return {
          changed: true,
          done: system.state.victory || !hasPendingEnemyTurn(system),
          type: "attack",
          unitId
        };
      }

      unit.hasMoved = true;
      unit.hasAttacked = true;
      appendLog(system.state, `${unit.name} staggered in place while zombified.`);
      return {
        changed: true,
        done: system.state.victory || system.state.enemyTurn.pendingUnitIds.length === 0,
        type: "wait",
        unitId
      };
    }

    if (canUnitSabotageDefendTarget(system.state, unit) && performSabotageDefendTarget(system.state, unit)) {
      system.recordPresentationEvent("mission", {
        action: "sabotage",
        actorId: unit.id,
        actorUnitTypeId: unit.unitTypeId,
        owner: unit.owner,
        x: unit.x,
        y: unit.y
      });
      system.updateVictoryState();
      return {
        changed: true,
        done: system.state.victory || system.state.enemyTurn.pendingUnitIds.length === 0,
        type: "sabotage",
        unitId
      };
    }

    const supportPlan = getBestSupportPlan(system.state, unit);

    if (supportPlan && system.applySupportAbility(unit, supportPlan.target)) {
      return {
        changed: true,
        done: system.state.victory || system.state.enemyTurn.pendingUnitIds.length === 0,
        type: "support",
        unitId
      };
    }

    const movementBudget = unit.stats.movement + getMovementModifier(system.state, unit);
    const reachableTiles = getReachableTiles(system.state, unit, movementBudget);
    const repairPlan = getBestRepairPlan(system.state, unit, reachableTiles);
    const capturePlan = getBestCapturePlan(system.state, unit, reachableTiles);
    const isGrunt = unit.unitTypeId === "grunt";
    const isSecondaryCapturer = ["breaker", "longshot"].includes(unit.unitTypeId);

    if (plannedAction?.unitId === unit.id) {
      if (plannedAction.type === "attack") {
        const movement = moveEnemyUnit(
          system,
          unit,
          plannedAction.tile,
          movementBudget
        );

        if (movement.moved) {
          system.state.enemyTurn.pendingAttack = {
            attackerId: unit.id,
            targetId: plannedAction.targetId,
            postAttackTile: plannedAction.postAttackTile
              ? { ...plannedAction.postAttackTile }
              : null
          };
          return {
            changed: true,
            done: system.state.victory || !hasPendingEnemyTurn(system),
            type: "move",
            unitId,
            moveSegments: movement.moveSegments
          };
        }

        if (system.attackTarget(unit.id, plannedAction.targetId)) {
          queueEnemySlipstreamMove(
            system,
            unit.id,
            plannedAction.postAttackTile ?? null
          );
          return {
            changed: true,
            done: system.state.victory || !hasPendingEnemyTurn(system),
            type: "attack",
            unitId
          };
        }
      }

      if (plannedAction.type === "capture") {
        const building = system.state.map.buildings.find(
          (candidate) => candidate.id === plannedAction.buildingId
        );

        if (building) {
          const result = resolveEnemyCapturePlan(
            system,
            unit,
            {
              building,
              tile: plannedAction.tile,
              canCaptureAfterMove: plannedAction.canCaptureAfterMove
            },
            movementBudget
          );

          if (result) {
            return result;
          }
        }
      }

      if (plannedAction.type === "repair") {
        const building = system.state.map.buildings.find(
          (candidate) => candidate.id === plannedAction.buildingId
        );

        if (building) {
          return resolveEnemyRepairPlan(
            system,
            unit,
            {
              building,
              tile: plannedAction.tile,
              canRepairAfterMove: plannedAction.canRepairAfterMove
            },
            movementBudget
          );
        }
      }
    }

    if (plannedAction?.unitId === unit.id) {
      clearCachedEnemyTurnPlan(system.state.enemyTurn);
    }

    if (isGrunt && capturePlan) {
      const result = resolveEnemyCapturePlan(system, unit, capturePlan, movementBudget);

      if (result) {
        return result;
      }
    }

    const immediateAttack = pickBestFavorableAttack(system.state, unit);

    if (immediateAttack) {
      system.attackTarget(unit.id, immediateAttack.target.id);
      queueEnemySlipstreamMove(system, unit.id);
      return {
        changed: true,
        done: system.state.victory || !hasPendingEnemyTurn(system),
        type: "attack",
        unitId
      };
    }

    if (repairPlan) {
      return resolveEnemyRepairPlan(system, unit, repairPlan, movementBudget);
    }

    if (isSecondaryCapturer && capturePlan) {
      const result = resolveEnemyCapturePlan(system, unit, capturePlan, movementBudget);

      if (result) {
        return result;
      }
    }

    if (unit.unitTypeId === "runner") {
      const transportPlan = getBestRunnerTransportPlan(system.state, unit, reachableTiles);
      const transportResult = resolveEnemyTransportPlan(system, unit, transportPlan, movementBudget);

      if (transportResult) {
        return transportResult;
      }
    }

    const moveAttackOption = getBestMoveAttackOption(system.state, unit, reachableTiles);
    const pinnedByThreat = isUnitPinnedByThreat(system.state, unit, reachableTiles);

    if (pinnedByThreat) {
      const pressuredAttack = pickBestAvailableAttack(system.state, unit);

      if (pressuredAttack && pressuredAttack.trade.dealtAverage >= 2) {
        system.attackTarget(unit.id, pressuredAttack.target.id);
        queueEnemySlipstreamMove(system, unit.id);
        return {
          changed: true,
          done: system.state.victory || !hasPendingEnemyTurn(system),
          type: "attack",
          unitId
        };
      }
    }

    const pressuredMoveAttack = pinnedByThreat
      ? getBestMoveAttackOption(system.state, unit, reachableTiles, { allowRisky: true })
      : null;
    const fallbackTile =
      moveAttackOption?.tile ??
      pressuredMoveAttack?.tile ??
      pickFallbackMovementTile(system.state, unit, reachableTiles);
    const movement = moveEnemyUnit(system, unit, fallbackTile, movementBudget);

    if (moveAttackOption && movement.moved) {
      system.state.enemyTurn.pendingAttack = {
        attackerId: unit.id,
        targetId: moveAttackOption.target.id
      };
      return {
        changed: true,
        done: system.state.victory || !hasPendingEnemyTurn(system),
        type: "move",
        unitId,
        moveSegments: movement.moveSegments
      };
    }

    if (pinnedByThreat) {
      if (
        pressuredMoveAttack &&
        movement.moved &&
        pressuredMoveAttack.tile.x === fallbackTile.x &&
        pressuredMoveAttack.tile.y === fallbackTile.y
      ) {
        system.state.enemyTurn.pendingAttack = {
          attackerId: unit.id,
          targetId: pressuredMoveAttack.target.id
        };
        return {
          changed: true,
          done: system.state.victory || !hasPendingEnemyTurn(system),
          type: "move",
          unitId,
          moveSegments: movement.moveSegments
        };
      }
    }

    if (movement.moved) {
      const unloaded = unit.unitTypeId === "runner" ? system.unloadTransportForEnemy(unit) : false;
      if (!unloaded) {
        unit.hasAttacked = true;
      }
      appendLog(
        system.state,
        `${unit.name} ${fallbackTile.intent === "fallback" ? "fell back from danger" : "advanced into position"}.`
      );
      return {
        changed: true,
        done: system.state.victory || system.state.enemyTurn.pendingUnitIds.length === 0,
        type: "move",
        unitId,
        moveSegments: movement.moveSegments
      };
    }
  }

  system.updateVictoryState();
  return { changed: false, done: true };
}

export function performEnemyEndTurnRecruitment(system) {
  if (!system.state.enemyTurn || !isEnemyTurnActive(system) || system.state.victory) {
    return {
      changed: false,
      deployments: []
    };
  }

  const deployments = performEnemyRecruitment(system);

  return {
    changed: deployments.length > 0,
    deployments
  };
}

export function prepareEnemyEndTurnReinforcements(system) {
  if (!system.state.enemyTurn || !isEnemyTurnActive(system) || system.state.victory) {
    return {
      changed: false,
      activations: [],
      deployments: []
    };
  }

  const activations = prepareEnemyTurnEndReinforcements(system.state);
  const pendingDeployments = activations.flatMap((activation) =>
    activation.queuedDeployments ?? []
  );

  system.state.enemyTurn.pendingReinforcementDeployments = pendingDeployments;

  return {
    changed: activations.length > 0,
    activations: activations.map((activation) => ({
      waveId: activation.waveId,
      activationNumber: activation.activationNumber,
      deployments: activation.deployments,
      skippedUnitCount: activation.skippedUnitCount
    })),
    deployments: pendingDeployments.map((deployment) => ({
      unitId: deployment.unitId,
      unitTypeId: deployment.unitTypeId,
      waveId: deployment.waveId,
      activationNumber: deployment.activationNumber,
      x: deployment.x,
      y: deployment.y
    }))
  };
}

export function hasPendingEnemyTurnReinforcements(system) {
  return Boolean(system.state.enemyTurn?.pendingReinforcementDeployments?.length);
}

export function processNextEnemyTurnReinforcement(system) {
  if (!system.state.enemyTurn || !isEnemyTurnActive(system) || system.state.victory) {
    return {
      changed: false,
      done: true,
      deployment: null
    };
  }

  const queue = system.state.enemyTurn.pendingReinforcementDeployments ?? [];
  const preparedDeployment = queue.shift();
  system.state.enemyTurn.pendingReinforcementDeployments = queue;

  const deployment = deployPreparedReinforcement(system.state, preparedDeployment);

  return {
    changed: Boolean(deployment),
    done: queue.length === 0,
    deployment
  };
}

export function shouldEnemyUsePower(system) {
  if (!system.state.enemyTurn || !isEnemyTurnActive(system) || system.state.victory) {
    return false;
  }

  const commander = getCommanderById(system.state.enemy.commanderId);
  const alwaysUseTypes = new Set([
    "atlas-overhaul",
    "viper-blitz-surge",
    "rook-hostile-takeover",
    "echo-disruption",
    "blaze-ignition",
    "knox-fortress-protocol",
    "falcon-air-strike",
    "graves-execution-window",
    "nova-overload",
    "sable-lucky-seven"
  ]);

  return alwaysUseTypes.has(commander?.active?.type) || hasEnemyAttackOpportunity(system.state);
}

export function finalizeEnemyTurn(system) {
  if (system.state.turn.activeSide !== TURN_SIDES.ENEMY) {
    return {
      changed: false,
      incomeGain: null
    };
  }

  system.state.enemyTurn = null;
  expireCurrentTurnStatuses(system.state, TURN_SIDES.ENEMY);
  applyMissionTurnEnd(system.state, TURN_SIDES.ENEMY);
  system.updateVictoryState();

  if (system.state.victory) {
    system.clearSelection();
    return {
      changed: true,
      incomeGain: null
    };
  }

  system.state.turn.activeSide = TURN_SIDES.PLAYER;
  const beforeStatusTick = snapshotUnitResources(system.state);
  let beforePlayerPassive = beforeStatusTick;
  tickSideStatuses(system.state, TURN_SIDES.PLAYER, {
    onAfterStatuses: () => {
      recordStatusAndPassiveChanges(system, beforeStatusTick, TURN_SIDES.PLAYER);
      beforePlayerPassive = snapshotUnitResources(system.state);
    }
  });
  recordStatusAndPassiveChanges(system, beforePlayerPassive, TURN_SIDES.PLAYER);
  tickUnitDurations(system, TURN_SIDES.PLAYER);
  const incomeGain = collectIncome(system, TURN_SIDES.PLAYER);
  resetActions(system, TURN_SIDES.PLAYER);
  const beforeRunCardEffects = snapshotUnitResources(system.state);
  const runCardNotes = applyRunCardTurnStartEffects(system.state, TURN_SIDES.PLAYER);
  recordRunCardResourceChanges(system, beforeRunCardEffects, TURN_SIDES.PLAYER);
  runCardNotes.forEach((note) => appendLog(system.state, note));
  removeDeadUnits(system.state);
  system.clearSelection();
  system.updateVictoryState();
  return {
    changed: true,
    incomeGain
  };
}

export function collectIncome(system, side) {
  if (system.state.mode === BATTLE_MODES.RUN && side === TURN_SIDES.PLAYER) {
    return {
      side,
      amount: 0,
      buildingIncome: 0,
      commanderBonus: 0,
      previousFunds: system.state[side].funds,
      nextFunds: system.state[side].funds
    };
  }

  const commanderBonus = getIncomeBonus(system.state, side);
  const incomeByType = system.state.economy?.incomeByType;
  const buildingIncome = getBuildingIncomeForSide(system.state.map.buildings, side, incomeByType);
  const previousFunds = system.state[side].funds;
  const amount = buildingIncome + commanderBonus;

  system.state[side].funds += amount;

  return {
    side,
    amount,
    buildingIncome,
    commanderBonus,
    previousFunds,
    nextFunds: system.state[side].funds
  };
}

export function resetActions(system, side) {
  for (const unit of getActionableUnitsForSide(system.state, side)) {
    unit.lastTurnMoved = Boolean(unit.movedThisTurn || unit.hasMoved);
    unit.hasMoved = false;
    unit.hasAttacked = false;
    unit.movedThisTurn = false;
  }
}

export function performEnemyRecruitment(system) {
  const deployments = [];
  const maxDeployments = getEnemyRecruitmentLimit(system.state);
  const mapRecruitCap = getEnemyRecruitmentMapCap(system.state);
  const playerUnitCount = getLivingUnits(system.state, TURN_SIDES.PLAYER).length;
  const productionSites = system.state.map.buildings.filter(
    (building) =>
      building.owner === TURN_SIDES.ENEMY &&
      PRODUCTION_BUILDING_TYPES.includes(building.type) &&
      !getUnitAt(system.state, building.x, building.y)
  );

  const usedBuildingIds = new Set();

  while (
    deployments.length < maxDeployments &&
    system.state.enemy.recruitsBuiltThisMap < mapRecruitCap
  ) {
    const enemyUnitLead = getLivingUnits(system.state, TURN_SIDES.ENEMY).length - playerUnitCount;

    if (enemyUnitLead >= ENEMY_RECRUITMENT_UNIT_LEAD_LIMIT) {
      break;
    }

    const candidate = pickEnemyRecruitmentCandidate(system.state, productionSites, usedBuildingIds);

    if (!candidate) {
      break;
    }

    const recruit = createUnitFromType(candidate.option.id, TURN_SIDES.ENEMY);
    recruit.x = candidate.building.x;
    recruit.y = candidate.building.y;
    recruit.hasMoved = true;
    recruit.hasAttacked = true;
    system.state.enemy.units.push(recruit);
    system.state.enemy.funds -= candidate.option.adjustedCost;
    system.state.enemy.recruitsBuiltThisMap += 1;
    usedBuildingIds.add(candidate.building.id);
    appendLog(system.state, `Enemy deployed ${recruit.name}.`);
    deployments.push({
      unitId: recruit.id,
      unitTypeId: recruit.unitTypeId,
      buildingId: candidate.building.id,
      x: recruit.x,
      y: recruit.y
    });
  }

  return deployments;
}

export function updateVictoryState(system) {
  resolveReinforcementTriggers(system.state);
  return updateMissionVictory(system.state);
}
